import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';

/**
 * Pre-data empty state with a soft brand-gradient frame, eyebrow pill,
 * primary/secondary CTAs and an optional preview-list panel. Mirrors
 * fresh-dashboard's EmptyState — same prop names so prototype code drops
 * in unchanged.
 *
 * `preview` items are { icon?, label, detail? }. They render as a
 * stacked list inside a card next to the copy.
 *
 * `primaryDisabled` renders the primary CTA inert (with `primaryHint`
 * underneath explaining why) — used while a launch is gated on something
 * the retailer is waiting for rather than something they can do.
 */
function EmptyState({
	icon: Icon = Sparkles,
	eyebrow = 'Coming soon',
	title,
	body,
	primaryCta,
	primaryHref = '/',
	onPrimaryClick,
	primaryDisabled = false,
	primaryHint,
	secondaryCta,
	secondaryHref,
	onSecondaryClick,
	preview,
	previewTitle
}) {
	return (
		<div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-soft p-8 md:p-12">
			<div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-gradient-unify opacity-20 blur-2xl" />
			<div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-gradient-unify opacity-10 blur-2xl" />

			<div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
				<div>
					<div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-white/80 px-3 py-1 backdrop-blur">
						<span className="h-1.5 w-1.5 rounded-full bg-gradient-unify" />
						<span className="text-[11px] font-bold uppercase tracking-[0.14em] text-foreground">{eyebrow}</span>
					</div>
					<h2 className="mt-4 text-2xl font-extrabold tracking-tight text-foreground md:text-[28px]">{title}</h2>
					<p className="mt-3 max-w-md text-base text-muted-foreground">{body}</p>

					<div className="mt-6 flex flex-wrap items-center gap-3">
						{primaryCta && primaryDisabled ? (
							<button
								type="button"
								disabled
								aria-disabled="true"
								className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-full bg-gradient-unify px-5 py-2.5 text-sm font-semibold text-white opacity-60"
							>
								{primaryCta} <ArrowRight className="h-3.5 w-3.5" />
							</button>
						) : primaryCta ? (
							onPrimaryClick ? (
								<button
									type="button"
									onClick={onPrimaryClick}
									className="inline-flex items-center gap-1.5 rounded-full bg-gradient-unify px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.5)] transition hover:opacity-90"
								>
									{primaryCta} <ArrowRight className="h-3.5 w-3.5" />
								</button>
							) : (
								<Link
									to={primaryHref}
									className="inline-flex items-center gap-1.5 rounded-full bg-gradient-unify px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.5)] transition hover:opacity-90"
								>
									{primaryCta} <ArrowRight className="h-3.5 w-3.5" />
								</Link>
							)
						) : null}
						{secondaryCta && onSecondaryClick ? (
							<button
								type="button"
								onClick={onSecondaryClick}
								className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-muted"
							>
								{secondaryCta} <ArrowRight className="h-3.5 w-3.5" />
							</button>
						) : null}
						{secondaryCta && !onSecondaryClick && secondaryHref ? (
							<Link
								to={secondaryHref}
								className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-muted"
							>
								{secondaryCta} <ArrowRight className="h-3.5 w-3.5" />
							</Link>
						) : null}
					</div>
					{primaryDisabled && primaryHint ? <p className="mt-3 text-sm text-muted-foreground">{primaryHint}</p> : null}
				</div>

				<div className="relative">
					<div className="relative rounded-2xl border border-border/60 bg-white/90 p-5 shadow-[0_20px_50px_-20px_rgba(79,70,229,0.35)] backdrop-blur md:p-6">
						<div className="flex items-center gap-3 border-b border-border/60 pb-4">
							<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-unify text-primary-foreground">
								<Icon className="h-5 w-5" />
							</div>
							<div>
								<div className="text-sm font-bold text-foreground">{previewTitle ?? "Here's what you'll see"}</div>
								<div className="text-xs text-muted-foreground">A preview — coming to life soon</div>
							</div>
						</div>
						<ul className="mt-4 space-y-3">
							{(preview ?? []).map((p, i) => {
								const PIcon = p.icon;
								return (
									<li
										key={i}
										className="flex items-center gap-3 rounded-xl border border-border/40 bg-muted/60 px-3.5 py-3"
									>
										{PIcon ? (
											<div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 bg-white text-muted-foreground">
												<PIcon className="h-4 w-4" />
											</div>
										) : (
											<div className="h-8 w-8 rounded-lg border border-border/60 bg-white" />
										)}
										<div className="min-w-0 flex-1">
											<div className="truncate text-sm font-semibold text-foreground">{p.label}</div>
											{p.detail ? <div className="truncate text-xs text-muted-foreground">{p.detail}</div> : null}
										</div>
										<div className="h-2 w-12 rounded-full bg-gradient-to-r from-muted to-transparent" />
									</li>
								);
							})}
						</ul>
					</div>
					<div className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-action text-action-foreground shadow-lg">
						<Sparkles className="h-4 w-4" />
					</div>
				</div>
			</div>
		</div>
	);
}

export { EmptyState };
